/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import * as emotion from "emotion";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {DB_Object, generateHex, PartialProperties} from "@intuitionrobotics/ts-common";
import {DB_Patient, GenderLabel, genders} from "@app/app-shared/patient";
import {DB_User, getUserNameWithTitle, PatientGender, UserRole} from "@app/ir-q-app-common/types/care-center/user"
import {GenericSelect} from "@intuitionrobotics/thunderstorm/app-frontend/components/GenericSelect";
import {DB_Organization} from "@app/ir-q-app-common/types/care-center/organization";
import {ICONS} from "@res/icons";
import {COLORS} from "@res/colors";
import {inputTextField} from "@styles/styles";
import {selectStyles} from "../SelectStyle";
import {OnUsersUpdated, UsersModule} from "@modules/UsersModule";
import {Loader} from "../../../../widgets/Loader";
import {OrganizationsContext, OrganizationsContextType} from "../organizations/OrganizationsContext";

type Props = {
    patient: PartialProperties<DB_Patient, keyof DB_Object | "contactId">
    onChange: (value: string | PatientGender, field: keyof DB_Patient) => void;
    existingPatient?: DB_Patient;
    viewOnly: boolean;
}

const inputText = emotion.css(
    {
        marginBottom: 15
    }
);

const inputTextLabel = emotion.css(
    {
        marginBottom: 5
    }
);
// "None" choice for GenericSelect.
const noneUser: DB_User = {
    _id: "",
    mail: "",
    first: "",
    last: ""
};

export class AddPatientDialogContent
    extends BaseComponent<Props, { loading: boolean }>
    implements OnUsersUpdated {


    constructor(props: Props) {
        super(props);
        this.state = this.getBaseState();
    }

    __onUsersUpdated() {
        this.stopLoader();
    }

    stopLoader = () => {
        const state = this.getBaseState();
        this.setState(state)
    }

    private getBaseState = () => ({loading: UsersModule.getUsers().length === 0});

    private renderNameField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Name:
            </div>
            <div>
                <input
                    readOnly={this.props.viewOnly}
                    className={inputTextField + " " + (this.props.existingPatient ? "disabled" : "")}
                    id={"patient-name"}
                    value={this.props.patient.name}
                    placeholder={"Name"}
                    onChange={(e) => this.props.onChange(e.target.value, "name")}
                    type={"text"}
                    name={generateHex(8)}
                    spellCheck={true}
                />
            </div>
        </div>;
    };

    private renderUnitField = () => {
        return <div className={`${inputText} ll_v_l`}>
            <div className={inputTextLabel}>
                Unit Id:
            </div>
            <div>
                <input
                    readOnly={this.props.viewOnly}
                    className={inputTextField + " " + (this.props.existingPatient ? "disabled" : "")}
                    id={"unit-id"}
                    name={generateHex(8)}
                    value={this.props.patient.unitId}
                    placeholder={"Unit Id"}
                    onChange={(e) => this.props.onChange(e.target.value, "unitId")}
                    type={"text"}
                    spellCheck={true}/>
            </div>
        </div>;
    };

    private renderOrganizationField = (orgCtx: OrganizationsContextType) => {
        const organizations = Object.values(orgCtx.organizations);
        return <div className={`${inputText} ll_v_l match_width`}>
            <div className={`${inputTextLabel}`}>
                Organization:
            </div>
            {
                organizations.length === 1 ?
                    <div>{organizations[0].name}</div> :
                    <GenericSelect<DB_Organization>
                        isDisabled={this.props.viewOnly}
                        options={organizations}
                        selectedOption={organizations.find(o => this.props.patient.organizationId === o._id)}
                        iconClose={ICONS.triangle_up(COLORS.grays_4, 12)}
                        iconOpen={ICONS.triangle_down(COLORS.grays_4, 12)}
                        onChange={(value) => this.props.onChange(value._id, "organizationId")}
                        styles={selectStyles}
                        presentation={(org: DB_Organization) => org.name}
                    />
            }
        </div>;
    };

    private renderCareManagerField = (careManagers: DB_User[]) => {
        careManagers.push(noneUser);
        return <div className={`${inputText} ll_v_l match_width`}>
            <div className={`${inputTextLabel}`}>
                Care manager:
            </div>
            <GenericSelect<DB_User>
                isDisabled={this.props.viewOnly}
                options={careManagers}
                selectedOption={careManagers.find(c => c._id === this.props.patient.careManagerId)}
                iconClose={ICONS.triangle_up(COLORS.grays_4, 12)}
                iconOpen={ICONS.triangle_down(COLORS.grays_4, 12)}
                onChange={(value) => this.props.onChange(value._id, "careManagerId")}
                styles={{
                    ...selectStyles, menu: (provided: any) => ({
                        ...provided,
                        bottom: 30,
                        top: "unset"
                    })
                }}
                presentation={(careManager: DB_User) => {
                    if (!careManager._id)
                        return "None";

                    return getUserNameWithTitle(careManager);
                }}
            />
        </div>;
    };

    private renderPhysicianField = (physicians: DB_User[]) => {
        physicians.push(noneUser);
        return <div className={`${inputText} ll_v_l match_width`}>
            <div className={inputTextLabel}>
                Physician:
            </div>
            <GenericSelect<DB_User>
                isDisabled={this.props.viewOnly}
                options={physicians}
                selectedOption={physicians.find(c => c._id === this.props.patient.physicianId)}
                iconClose={ICONS.triangle_up(COLORS.grays_4, 12)}
                iconOpen={ICONS.triangle_down(COLORS.grays_4, 12)}
                onChange={(value) => this.props.onChange(value._id, "physicianId")}
                styles={{
                    ...selectStyles, menu: (provided: any) => ({
                        ...provided,
                        bottom: 30,
                        top: "unset"
                    })
                }}
                presentation={(physician: DB_User) => {
                    if (!physician._id)
                        return "None";

                    return getUserNameWithTitle(physician);
                }}
            />
        </div>;
    };

    private renderGenderField = () => {
        return <div className={`${inputText} ll_v_l match_width`}>
            <div className={inputTextLabel}>
                Gender:
            </div>
            <GenericSelect<GenderLabel>
                isDisabled={this.props.viewOnly}
                options={genders}
                selectedOption={genders.find(g => g.key === this.props.patient.gender)}
                iconClose={ICONS.triangle_up(COLORS.grays_4, 12)}
                iconOpen={ICONS.triangle_down(COLORS.grays_4, 12)}
                onChange={(value) => this.props.onChange(value.key, "gender")}
                styles={{
                    ...selectStyles, menu: (provided: any) => ({
                        ...provided,
                        bottom: 30,
                        top: "unset"
                    })
                }}
                presentation={(gender: GenderLabel) => gender?.label ?? "Select something"}
            />
        </div>;
    };

    render() {
        return <>
            <div className={`ll_v_c`}>
                {this.getCareCenterFields()}
            </div>
        </>
    }

    private getCareCenterFields() {
        if (this.state.loading)
            return <Loader/>

        const users = UsersModule.getUsers();
        const filterUsers = (key: UserRole) => users.filter(user => user.roles?.includes(key))
        const careManagers = filterUsers(UserRole.CareManager);
        const physicians = filterUsers(UserRole.Physician);

        return <OrganizationsContext.Consumer>
            {orgCtx => {
                return <>
                    {this.renderNameField()}
                    {this.renderUnitField()}
                    {this.renderOrganizationField(orgCtx)}
                    {this.renderCareManagerField(careManagers)}
                    {this.renderPhysicianField(physicians)}
                    {this.renderGenderField()}
                </>
            }}
        </OrganizationsContext.Consumer>;
    }
}
